(() => {
    class StarinaHeroSection extends HTMLElement {
        connectedCallback() {
            this.detailDuration = Number(this.dataset.detailDuration || 350);
            this.closeDelay = Number(this.dataset.closeDelay || 1500);

            this.activeId = "default";
            this.closeTimer = null;
            this.leaveTimers = new WeakMap();

            this.mobileMediaQuery = window.matchMedia("(max-width: 999px)");
            this.isMobile = this.mobileMediaQuery.matches;

            this.cards = Array.from(this.querySelectorAll("[data-card-panel]"));
            this.details = Array.from(this.querySelectorAll(".starina-hero-section__detail"));
            this.mediaItems = Array.from(this.querySelectorAll(".starina-hero-section__media"));
            this.defaultView = this.querySelector('.starina-hero-section__default[data-panel="default"]');
            this.resetZones = Array.from(this.querySelectorAll("[data-reset-zone]")).filter((element) => element.dataset.resetZone !== "false");

            this.handleMediaQueryChange = this.handleMediaQueryChange.bind(this);
            this.handleComponentClick = this.handleComponentClick.bind(this);
            this.handleComponentPointerOver = this.handleComponentPointerOver.bind(this);
            this.handleComponentFocusIn = this.handleComponentFocusIn.bind(this);
            this.handleComponentPointerLeave = this.handleComponentPointerLeave.bind(this);
            this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
            this.handleEscapeKey = this.handleEscapeKey.bind(this);

            this.addEventListener("click", this.handleComponentClick);
            this.addEventListener("pointerover", this.handleComponentPointerOver);
            this.addEventListener("focusin", this.handleComponentFocusIn);
            this.addEventListener("pointerleave", this.handleComponentPointerLeave);

            document.addEventListener("pointerdown", this.handleDocumentPointerDown, true);
            document.addEventListener("keydown", this.handleEscapeKey, true);

            if (this.mobileMediaQuery.addEventListener) {
                this.mobileMediaQuery.addEventListener("change", this.handleMediaQueryChange);
            } else {
                this.mobileMediaQuery.addListener(this.handleMediaQueryChange);
            }

            this.setState("default", { immediate: true });
        }

        disconnectedCallback() {
            this.clearCloseTimer();

            this.details.forEach((detail) => this.clearLeaveTimer(detail));

            this.removeEventListener("click", this.handleComponentClick);
            this.removeEventListener("pointerover", this.handleComponentPointerOver);
            this.removeEventListener("focusin", this.handleComponentFocusIn);
            this.removeEventListener("pointerleave", this.handleComponentPointerLeave);

            document.removeEventListener("pointerdown", this.handleDocumentPointerDown, true);
            document.removeEventListener("keydown", this.handleEscapeKey, true);

            if (this.mobileMediaQuery?.removeEventListener) {
                this.mobileMediaQuery.removeEventListener("change", this.handleMediaQueryChange);
            } else if (this.mobileMediaQuery?.removeListener) {
                this.mobileMediaQuery.removeListener(this.handleMediaQueryChange);
            }
        }

        handleMediaQueryChange(event) {
            this.isMobile = event.matches;
            this.clearCloseTimer();
            this.setState("default", { immediate: true });
        }

        handleComponentClick(event) {
            const cta = event.target.closest(".starina-hero-section__cta");

            if (cta) return;

            const card = event.target.closest("[data-card-panel]");

            if (!card || !this.contains(card)) return;

            const id = card.dataset.cardPanel;
            if (!id) return;

            this.clearCloseTimer();

            if (this.isMobile) {
                if (this.activeId === id) {
                    this.setState("default");
                } else {
                    this.setState(id);
                }

                return;
            }

            this.setState(id);
        }

        handleComponentPointerOver(event) {
            if (this.isMobile) return;

            const cta = event.target.closest(".starina-hero-section__cta");

            if (cta) {
                this.clearCloseTimer();
                return;
            }

            const card = event.target.closest("[data-card-panel]");
            if (card && this.contains(card)) {
                const id = card.dataset.cardPanel;
                if (!id) return;

                this.clearCloseTimer();
                this.setState(id);
                return;
            }

            const detail = event.target.closest(".starina-hero-section__detail");
            if (detail && this.contains(detail)) {
                this.clearCloseTimer();
                return;
            }

            const resetZone = event.target.closest("[data-reset-zone]");
            if (resetZone && this.contains(resetZone) && resetZone.dataset.resetZone !== "false") {
                this.scheduleClose();
            }
        }

        handleComponentFocusIn(event) {
            const cta = event.target.closest(".starina-hero-section__cta");

            if (cta) {
                this.clearCloseTimer();
                return;
            }

            if (this.isMobile) {
                return;
            }

            const card = event.target.closest("[data-card-panel]");
            if (card && this.contains(card)) {
                const id = card.dataset.cardPanel;
                if (!id) return;

                this.clearCloseTimer();
                this.setState(id);
                return;
            }

            const detail = event.target.closest(".starina-hero-section__detail");
            if (detail && this.contains(detail)) {
                this.clearCloseTimer();
                return;
            }

            const resetZone = event.target.closest("[data-reset-zone]");
            if (resetZone && this.contains(resetZone) && resetZone.dataset.resetZone !== "false") {
                this.scheduleClose();
            }
        }

        handleComponentPointerLeave() {
            if (this.isMobile) return;
            this.scheduleClose();
        }

        handleDocumentPointerDown(event) {
            if (this.activeId === "default") return;
            if (this.contains(event.target)) return;

            this.clearCloseTimer();
            this.setState("default", { immediate: true });
        }

        handleEscapeKey(event) {
            if (event.key !== "Escape") return;
            if (this.activeId === "default") return;

            this.clearCloseTimer();
            this.setState("default", { immediate: true });
        }

        clearCloseTimer() {
            if (this.closeTimer) {
                window.clearTimeout(this.closeTimer);
                this.closeTimer = null;
            }
        }

        scheduleClose() {
            if (this.isMobile) return;
            if (this.activeId === "default") return;

            this.clearCloseTimer();

            this.closeTimer = window.setTimeout(() => {
                this.setState("default");
            }, this.closeDelay);
        }

        clearLeaveTimer(detail) {
            const timer = this.leaveTimers.get(detail);

            if (timer) {
                window.clearTimeout(timer);
                this.leaveTimers.delete(detail);
            }
        }

        startLeaving(detail) {
            if (!detail) return;

            this.clearLeaveTimer(detail);
            detail.classList.remove("is-active");
            detail.classList.add("is-leaving");

            const timer = window.setTimeout(() => {
                detail.classList.remove("is-leaving");
                this.leaveTimers.delete(detail);
            }, this.detailDuration);

            this.leaveTimers.set(detail, timer);
        }

        setState(nextId, { immediate = false } = {}) {
            if (!nextId) nextId = "default";
            if (nextId === this.activeId && !immediate) return;

            const previousId = this.activeId;
            this.activeId = nextId;

            this.updateCards();
            this.updateDefaultView();
            this.updateMedia();
            this.updateDetails(previousId, nextId, immediate);
        }

        updateCards() {
            this.cards.forEach((card) => {
                const isActive = card.dataset.cardPanel === this.activeId;
                card.classList.toggle("is-active", isActive);

                const trigger = card.querySelector("[data-card-trigger]");
                if (trigger) {
                    trigger.setAttribute("aria-expanded", isActive ? "true" : "false");
                }
            });
        }

        updateDefaultView() {
            if (!this.defaultView) return;
            this.defaultView.classList.toggle("is-active", this.activeId === "default");
        }

        updateMedia() {
            this.mediaItems.forEach((media) => {
                const isActive = media.dataset.media === this.activeId;
                media.classList.toggle("is-active", isActive);
            });
        }

        updateDetails(previousId, nextId, immediate = false) {
            const previousDetail = this.getDetail(previousId);
            const nextDetail = this.getDetail(nextId);

            if (immediate) {
                this.details.forEach((detail) => {
                    this.clearLeaveTimer(detail);
                    detail.classList.remove("is-active", "is-leaving");
                });

                if (nextDetail) {
                    nextDetail.classList.add("is-active");
                }

                return;
            }

            if (previousDetail && previousDetail !== nextDetail) {
                this.startLeaving(previousDetail);
            }

            this.details.forEach((detail) => {
                if (detail === nextDetail) {
                    this.clearLeaveTimer(detail);
                    detail.classList.remove("is-leaving");
                    detail.classList.add("is-active");
                    return;
                }

                if (detail !== previousDetail && !detail.classList.contains("is-leaving")) {
                    detail.classList.remove("is-active");
                }
            });

            if (!nextDetail) {
                this.details.forEach((detail) => {
                    if (detail !== previousDetail && !detail.classList.contains("is-leaving")) {
                        detail.classList.remove("is-active");
                    }
                });
            }
        }

        getDetail(id) {
            if (!id || id === "default") return null;
            return this.querySelector(`.starina-hero-section__detail[data-panel="${id}"]`);
        }
    }

    if (!window.customElements.get("starina-hero-section")) {
        window.customElements.define("starina-hero-section", StarinaHeroSection);
    }
})();
